/**
 * Help API Service
 * API calls for help articles, categories, and search
 */

import { api } from './api';
import type {
  HelpCategory,
  HelpArticle,
  HelpArticleSummary,
  RelatedArticle,
  ListArticlesRequest,
  ListArticlesResponse,
  GetArticleResponse,
  GetRelatedArticlesResponse,
  RecordFeedbackRequest,
  RecordFeedbackResponse,
  SearchResponse,
} from '../types/help.types';

// Base URL for help endpoints
const HELP_URL = '/help';

/**
 * Get all help categories
 */
export const getCategories = async (): Promise<HelpCategory[]> => {
  const response = await api.get<{ data: { categories: HelpCategory[] } }>(
    `${HELP_URL}/categories`
  );
  return response.data.data.categories;
};

/**
 * Get help articles with filters and pagination
 */
export const getArticles = async (
  params?: ListArticlesRequest
): Promise<ListArticlesResponse> => {
  const searchParams = new URLSearchParams();

  if (params) {
    if (params.category) {
      searchParams.append('category', params.category);
    }
    if (params.type) {
      searchParams.append('type', params.type);
    }
    if (params.search) {
      searchParams.append('search', params.search);
    }
    if (params.tier) {
      searchParams.append('tier', params.tier);
    }
    if (params.page) {
      searchParams.append('page', params.page);
    }
    if (params.limit) {
      searchParams.append('limit', params.limit.toString());
    }
    if (params.offset !== undefined) {
      searchParams.append('offset', params.offset.toString());
    }
  }

  const queryString = searchParams.toString();
  const url = queryString ? `${HELP_URL}?${queryString}` : HELP_URL;

  const response = await api.get<{ data: ListArticlesResponse }>(url);
  return response.data.data;
};

/**
 * Get a single help article by slug
 */
export const getArticle = async (slug: string): Promise<HelpArticle> => {
  const response = await api.get<{ data: GetArticleResponse }>(
    `${HELP_URL}/${slug}`
  );
  return response.data.data.article;
};

/**
 * Get articles by category
 */
export const getArticlesByCategory = async (
  category: string
): Promise<HelpArticle[]> => {
  const response = await api.get<{ data: { articles: HelpArticle[] } }>(
    `${HELP_URL}/category/${category}`
  );
  return response.data.data.articles;
};

/**
 * Get contextual articles for a specific page and tier
 */
export const getContextualArticles = async (
  page: string,
  tier: string
): Promise<HelpArticleSummary[]> => {
  const response = await api.get<{ data: { articles: HelpArticleSummary[] } }>(
    `${HELP_URL}/contextual`,
    {
      params: { page, tier },
    }
  );
  return response.data.data.articles;
};

/**
 * Get related articles for a specific article
 */
export const getRelatedArticles = async (
  slug: string
): Promise<RelatedArticle[]> => {
  const response = await api.get<{ data: GetRelatedArticlesResponse }>(
    `${HELP_URL}/${slug}/related`
  );
  return response.data.data.articles;
};

/**
 * Submit feedback on an article (helpful/not helpful)
 */
export const submitFeedback = async (
  articleId: string,
  helpful: boolean
): Promise<RecordFeedbackResponse> => {
  const response = await api.post<{ data: RecordFeedbackResponse }>(
    `${HELP_URL}/articles/${articleId}/feedback`,
    { helpful } as RecordFeedbackRequest
  );
  return response.data.data;
};

/**
 * Record article view
 */
export const recordArticleView = async (articleId: string): Promise<void> => {
  await api.post(`${HELP_URL}/articles/${articleId}/view`);
};

/**
 * Search help articles
 */
export const searchArticles = async (query: string): Promise<SearchResponse> => {
  const response = await api.get<{ data: SearchResponse }>(
    `${HELP_URL}/search`,
    {
      params: { q: query },
    }
  );
  return response.data.data;
};

/**
 * Get search suggestions (autocomplete)
 */
export const getSuggestions = async (query: string): Promise<string[]> => {
  const response = await api.get<{ data: { suggestions: string[] } }>(
    `${HELP_URL}/suggestions`,
    {
      params: { q: query },
    }
  );
  return response.data.data.suggestions;
};

/**
 * Get popular articles
 */
export const getPopularArticles = async (
  limit: number = 10
): Promise<HelpArticleSummary[]> => {
  const response = await api.get<{ data: { articles: HelpArticleSummary[] } }>(
    `${HELP_URL}/popular`,
    {
      params: { limit },
    }
  );
  return response.data.data.articles;
};

/**
 * Get recently updated articles
 */
export const getRecentArticles = async (
  limit: number = 10
): Promise<HelpArticleSummary[]> => {
  const response = await api.get<{ data: { articles: HelpArticleSummary[] } }>(
    `${HELP_URL}/recent`,
    {
      params: { limit },
    }
  );
  return response.data.data.articles;
};

// Export all functions as a service object
export const helpApi = {
  getCategories,
  getArticles,
  getArticle,
  getArticlesByCategory,
  getContextualArticles,
  getRelatedArticles,
  submitFeedback,
  recordArticleView,
  searchArticles,
  getSuggestions,
  getPopularArticles,
  getRecentArticles,
};

export default helpApi;
